export const SEGMENTATION_DEV_SNAPSHOT_VERSION = 1 as const;

export interface Vec3Snapshot {
  x: number;
  y: number;
  z: number;
}

export interface SegmentationAnchorSnapshot {
  position: Vec3Snapshot;
  vertexRenderId: number | null;
  cameraPosition?: Vec3Snapshot;
}

export interface SegmentationSnapPartnerSnapshot {
  localIndex: number;
  partnerToothIndex: number;
  partnerLocalIndex: number;
}

export interface SegmentationToothBoundarySnapshot {
  controlPoints: Vec3Snapshot[];
  snapPartners: SegmentationSnapPartnerSnapshot[];
}

export interface SegmentationToothDevSnapshot {
  anchor: SegmentationAnchorSnapshot;
  boundaryCurve: SegmentationToothBoundarySnapshot;
}

/** Saved segmentation input for one jaw — anchors + edited tooth boundary curves. */
export interface SegmentationJawDevSnapshot {
  version: typeof SEGMENTATION_DEV_SNAPSHOT_VERSION;
  scanId: string | null;
  jaw: 'upper' | 'lower';
  startStop: number;
  endStop: number;
  missingAfter: number[];
  teeth: SegmentationToothDevSnapshot[];
}

export function vec3ToSnapshot(v: { x: number; y: number; z: number }): Vec3Snapshot {
  return { x: v.x, y: v.y, z: v.z };
}

export function devSnapshotStorageKey(scanId: string | null, jaw: 'upper' | 'lower'): string {
  return `zubee-segmentation-dev:${scanId ?? 'unknown'}:${jaw}`;
}

/** One-step rollback snapshot per jaw (production flow — separate from dev dock). */
export function rollbackSnapshotStorageKey(scanId: string | null, jaw: 'upper' | 'lower'): string {
  return `zubee-segmentation-rollback:${scanId ?? 'unknown'}:${jaw}`;
}

export function saveRollbackSnapshotToLocalStorage(snapshot: SegmentationJawDevSnapshot): void {
  localStorage.setItem(rollbackSnapshotStorageKey(snapshot.scanId, snapshot.jaw), JSON.stringify(snapshot));
}

export function loadRollbackSnapshotFromLocalStorage(
  scanId: string | null,
  jaw: 'upper' | 'lower',
): SegmentationJawDevSnapshot | null {
  const raw = localStorage.getItem(rollbackSnapshotStorageKey(scanId, jaw));
  if (!raw) return null;
  try {
    return JSON.parse(raw) as SegmentationJawDevSnapshot;
  } catch {
    return null;
  }
}

export function downloadJson(filename: string, data: unknown): void {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

export function saveDevSnapshotToLocalStorage(snapshot: SegmentationJawDevSnapshot): void {
  localStorage.setItem(devSnapshotStorageKey(snapshot.scanId, snapshot.jaw), JSON.stringify(snapshot));
}

export function loadDevSnapshotFromLocalStorage(
  scanId: string | null,
  jaw: 'upper' | 'lower',
): SegmentationJawDevSnapshot | null {
  const raw = localStorage.getItem(devSnapshotStorageKey(scanId, jaw));
  if (!raw) return null;
  try {
    return JSON.parse(raw) as SegmentationJawDevSnapshot;
  } catch {
    return null;
  }
}

export async function pickDevSnapshotFile(): Promise<SegmentationJawDevSnapshot | null> {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'application/json,.json';
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) {
        resolve(null);
        return;
      }
      try {
        const text = await file.text();
        resolve(JSON.parse(text) as SegmentationJawDevSnapshot);
      } catch {
        resolve(null);
      }
    };
    input.click();
  });
}
